// Ruevale — mobile product gallery.
// 1. Keeps the highlighted thumbnail visible: when Horizon marks a thumbnail as selected
//    (after a swipe, a tap or a color change), scroll the thumbnail row sideways to it.
// 2. After a color change, Horizon swaps in a new slideshow whose `initial-slide` is the
//    selected color's image, but it measures slide positions before the new layout is ready,
//    so the carousel can stay on the first image. Once layout has settled, scroll the
//    carousel to that image.
// 3. Settles the carousel on an exact position whenever a swipe or a scroll stops: first image
//    flush left, last image flush right, the others centered, so the peeks stay equal on every
//    slide. CSS scroll snapping should do this, but iOS Safari doesn't always re-snap after
//    Horizon's scripted scrolls, and the small misses added up from slide to slide.
// Only the row and the carousel scroll; the page never jumps.

const GALLERY = '.product-information media-gallery';
const THUMB = `${GALLERY} .slideshow-controls__thumbnail`;
const mobile = window.matchMedia('(max-width: 749px)');
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function revealThumbnail(thumb) {
  const row = thumb.closest('.slideshow-controls__thumbnails-container');
  if (!row || row.scrollWidth <= row.clientWidth) return;
  const target = thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2;
  row.scrollTo({ left: Math.max(0, target), behavior: reduceMotion() ? 'auto' : 'smooth' });
}

/** Exact scroll position of each slide: first flush left, last flush right, the others centered. */
function slidePositions(scroller) {
  const slides = [...scroller.querySelectorAll('slideshow-slide')].filter((slide) => slide.offsetParent);
  const box = scroller.getBoundingClientRect();
  const max = scroller.scrollWidth - scroller.clientWidth;
  return slides.map((slide, index) => {
    const rect = slide.getBoundingClientRect();
    const start = scroller.scrollLeft + rect.left - box.left - scroller.clientLeft;
    const free = scroller.clientWidth - rect.width;
    const target = index === 0 ? start : index === slides.length - 1 ? start - free : start - free / 2;
    return Math.min(Math.max(target, 0), max);
  });
}

function syncCarousel(slideshow) {
  const index = Number(slideshow.getAttribute('initial-slide')) || 0;
  if (index === 0) return;

  // Wait two frames so the swapped-in slides have their final layout.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const scroller = slideshow.querySelector('slideshow-slides');
      const target = scroller && slidePositions(scroller)[index];
      if (target === undefined || Math.abs(scroller.scrollLeft - target) <= 1) return;
      scroller.scrollTo({ left: target, behavior: 'instant' });
    })
  );
}

/* Settle on an exact position once the carousel stops moving and no finger is down. */
let touching = false;
const settleTimers = new WeakMap();

function settle(scroller) {
  if (touching || !mobile.matches || !scroller.isConnected) return;
  if (scroller.closest('slideshow-component')?.hasAttribute('dragging')) return;
  const current = scroller.scrollLeft;
  const nearest = slidePositions(scroller).reduce(
    (best, position) => (Math.abs(position - current) < Math.abs(best - current) ? position : best),
    Infinity
  );
  if (Number.isFinite(nearest) && Math.abs(nearest - current) > 1) {
    scroller.scrollTo({ left: nearest, behavior: reduceMotion() ? 'auto' : 'smooth' });
  }
}

function scheduleSettle(scroller) {
  clearTimeout(settleTimers.get(scroller));
  settleTimers.set(scroller, setTimeout(() => settle(scroller), 180));
}

const galleryScroller = (target) =>
  target instanceof HTMLElement && target.matches(`${GALLERY} slideshow-slides`) ? target : null;

document.addEventListener(
  'scroll',
  (event) => {
    const scroller = galleryScroller(event.target);
    if (scroller) scheduleSettle(scroller);
  },
  { capture: true, passive: true }
);
document.addEventListener('touchstart', () => (touching = true), { capture: true, passive: true });
for (const type of ['touchend', 'touchcancel']) {
  document.addEventListener(
    type,
    (event) => {
      touching = event.touches.length > 0;
      const scroller = event.target instanceof Element && event.target.closest(`${GALLERY} slideshow-slides`);
      if (scroller && !touching) scheduleSettle(scroller);
    },
    { capture: true, passive: true }
  );
}

new MutationObserver((mutations) => {
  if (!mobile.matches) return;

  for (const mutation of mutations) {
    const target = mutation.target;

    if (mutation.type === 'attributes' && target instanceof HTMLElement) {
      if (mutation.attributeName === 'aria-selected' && target.matches(THUMB) && target.getAttribute('aria-selected') === 'true') {
        revealThumbnail(target);
      } else if (mutation.attributeName === 'initial-slide' && target.closest(GALLERY)) {
        syncCarousel(target);
      }
    }

    if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        const slideshow = node.matches('slideshow-component') ? node : node.querySelector?.('slideshow-component');
        if (slideshow && slideshow.closest(GALLERY)) syncCarousel(slideshow);
      }
    }
  }
}).observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['aria-selected', 'initial-slide'],
});

// Opening a product URL with ?variant= also starts on that variant's image.
const syncAll = () => {
  if (!mobile.matches) return;
  document.querySelectorAll(`${GALLERY} slideshow-component`).forEach(syncCarousel);
};
if (document.readyState === 'complete') syncAll();
else window.addEventListener('load', syncAll, { once: true });
